import * as Sentry from "@sentry/react";
import { Button, Form } from "antd";
import { FormInstance } from "antd/lib/form";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CustomInput } from "../../components";
import localStorageKey from "../../enumerations/localStorageKey";
import { usePostEmailSend, useSignUp } from "../../hooks/SignUp/signup.hooks";
import styles from "./style.module.scss";

const SignUp = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { mutate: signup } = useSignUp();
  const { mutate: emailSend } = usePostEmailSend();
  const [isLoading, setIsLoading] = useState(false);
  const [values, setValues] = useState({
    account: "",
    password: "",
    confirm_password: "",
  });

  const onFinish = async () => {
    setIsLoading(true);
    const field: any = {
      account: values.account,
      password: values.password,
      confirm_password: values.confirm_password,
    };

    signup(
      { params: field },
      {
        onSuccess: (res: any) => {
          const data = res.data.data;

          window.localStorage.setItem(localStorageKey.TOKEN, data.token);
          window.localStorage.setItem(
            localStorageKey.USER_ACCOUNT,
            data.user.email
          );

          Sentry.setUser({
            id: data.user.account_id,
            username: data.user.email,
          });

          const token = window.localStorage.getItem(localStorageKey.TOKEN);

          if (token) {
            emailSend();

            setTimeout(() => {
              navigate("/verify");
            }, 1000);
          }
          setIsLoading(false);
        },
        onError: (error: any) => {
          console.debug(error);
          setIsLoading(false);
        },
      }
    );
  };

  const handleChange = (prop: any) => (event: any) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  return (
    <div className={styles.signUpContainer}>
      <div className={styles.signUpTitle}>{t("signup.quickSignup")}</div>
      <div className={styles.signupWrapper}>
        <div className={styles.inputContainer}>
          <Form
            form={form}
            name="register"
            initialValues={{}}
            autoComplete="off"
            requiredMark={false}
            layout="vertical"
            onFinish={onFinish}
          >
            <CustomInput
              formItemProps={{
                name: "account",
                label: t("normal.email"),
                rules: [
                  {
                    required: true,
                    message: t("form.required", { name: t("normal.email") }),
                  },
                  {
                    pattern:
                      /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/,
                    message: t("signup.emailFormat.error"),
                  },
                  {
                    max: 100,
                    message: t("normal.maxLength", { max: 100 }),
                  },
                ],
              }}
              inputProps={{
                placeholder: t("signup.enterInput", { content: "" }),
                onChange: handleChange("account"),
              }}
            />
            <CustomInput
              formItemProps={{
                name: "password",
                label: t("normal.password"),
                rules: [
                  {
                    required: true,
                    message: t("form.required", { name: t("normal.password") }),
                  },
                  {
                    min: 6,
                    max: 18,
                    pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,18}$/,
                    message: t("normal.password.validation.error"),
                  },
                ],
              }}
              formItemType="password"
              inputProps={{
                onChange: (e) => {
                  handleChange("password")(e);
                  form.validateFields(["confirm_password"]);
                },
                placeholder: t("signup.enterInput", {
                  content: t("signup.enterPasswordFormat"),
                }),
              }}
            />
            <CustomInput
              formItemProps={{
                name: "confirm_password",
                label: t("signup.confirmPassword"),
                rules: [
                  {
                    required: true,
                    message: t("form.required", {
                      name: t("signup.confirmPassword"),
                    }),
                  },
                  ({
                    getFieldValue,
                  }: {
                    getFieldValue: FormInstance["getFieldValue"];
                  }) => ({
                    validator(_: any, value: any) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(t("signup.confirmPasswordError"))
                      );
                    },
                  }),
                ],
              }}
              formItemType="password"
              inputProps={{
                onChange: handleChange("confirm_password"),
                placeholder: t("signup.enterPasswordAgain"),
              }}
            />
            <div>
              <span>{t("signup.agreement")}</span>
              <a
                aria-label={t("signup.userTerms")}
                href="/terms"
                target="_blank"
              >
                {t("normal.termsOfService")}
              </a>
              <span>{t("normal.and")}</span>
              <a
                aria-label={t("signup.privacyPolicy")}
                href="/privacypolicy"
                target="_blank"
              >
                {t("signup.privacyPolicy", { symbol: "。" })}
              </a>
            </div>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              disabled={isLoading}
              loading={isLoading}
              style={{
                width: "100%",
                borderRadius: "100px",
                height: "52px",
                marginTop: "29px",
              }}
            >
              {t("normal.register")}
            </Button>
          </Form>
        </div>
        <div className={styles.privacy}>
          <span>{t("signup.gotAccount")}</span>
          <span
            className={styles.privacyLogin}
            onClick={() => {
              navigate("/signin");
            }}
          >
            {t("normal.siginin")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
